import { computed } from "vue";
import EmployeeSecurity from "@/enumerations/Security/EmployeeSecurity";
import YesNoSecurity from "@/enumerations/Security/YesNoSecurity";
import { usePortalSecuritySettings } from "@/composables/Security/usePortalSecuritySettings";
import { useAuthStore } from "@/stores/auth";
import { useParametersStore } from "@/stores/parameters";

export function useCommoditySecuritySettings() {
  const authStore = useAuthStore();
  const parametersStore = useParametersStore();
  const portalSecuritySettings = usePortalSecuritySettings();

  function checkUserProfileBasedSecurity(
    currentSecurityLevel: number | undefined,
    minimumSecurityLevel: number
  ) {
    if (
      authStore.webLogin.isEmployee &&
      authStore.webLogin.employeeSecurity === EmployeeSecurity.userProfile &&
      !authStore.selectedAccount?.isAccountLinkedToWebLogin
    ) {
      return (currentSecurityLevel ?? 0) >= minimumSecurityLevel;
    }

    return true;
  }

  const showCommodity = computed(() => {
    return (
      !portalSecuritySettings.managerAccountSelected.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.commodity,
        YesNoSecurity.yes
      )
    );
  });

  const showCashBidsPage = computed(() => {
    return (
      parametersStore.parameterSettings.showCgBids &&
      showCommodity.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.viewCashBids,
        YesNoSecurity.yes
      )
    );
  });

  const showBalancesPage = computed(() => {
    return (
      parametersStore.parameterSettings.showCgBalances &&
      showCommodity.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.viewCommodityBalances,
        YesNoSecurity.yes
      )
    );
  });

  const showActivityPage = computed(() => {
    return (
      parametersStore.parameterSettings.showCgActivity &&
      showCommodity.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.viewCommodityActivity,
        YesNoSecurity.yes
      )
    );
  });

  const showContractsPage = computed(() => {
    return (
      parametersStore.parameterSettings.showCgContracts &&
      showCommodity.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.viewCommodityContracts,
        YesNoSecurity.yes
      )
    );
  });

  const showOffersPage = computed(() => {
    return (
      parametersStore.parameterSettings.showCgOffers &&
      showCommodity.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.viewCommodityContracts,
        YesNoSecurity.yes
      )
    );
  });

  const showTicketsPage = computed(() => {
    return (
      parametersStore.parameterSettings.showCgTickets &&
      showCommodity.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.viewCommodityTickets,
        YesNoSecurity.yes
      )
    );
  });

  const showGrainBankPage = computed(() => {
    return (
      parametersStore.parameterSettings.showCgGrainBank &&
      showCommodity.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.viewCommodityGrainBank,
        YesNoSecurity.yes
      )
    );
  });

  const showProofOfYieldPage = computed(() => {
    return (
      parametersStore.parameterSettings.showCgProofOfYield &&
      showCommodity.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.viewCommodityProofOfYield,
        YesNoSecurity.yes
      )
    );
  });

  const showCommodityMenu = computed(() => {
    return (
      parametersStore.parameterSettings.showCgMenu &&
      showCommodity.value &&
      (showCashBidsPage.value ||
        showBalancesPage.value ||
        showActivityPage.value ||
        showContractsPage.value ||
        showOffersPage.value ||
        showTicketsPage.value ||
        showGrainBankPage.value ||
        showProofOfYieldPage.value)
    );
  });

  return {
    showActivityPage,
    showBalancesPage,
    showCashBidsPage,
    showCommodity,
    showCommodityMenu,
    showContractsPage,
    showGrainBankPage,
    showOffersPage,
    showProofOfYieldPage,
    showTicketsPage,
  };
}
